import { IconButton, Tooltip, styled } from '@mui/material';
import cn from 'classnames';
import type { IEmoji } from 'src/@types/chat';
import { useLocales } from 'src/locales';
import { PopoverTrigger } from '../PopoverTrigger';
import Iconify from '../iconify';
import { EmojiPickerPopover } from './EmojiPickerPopover';

export type EmojiHandlerPopoverProps = {
  onSelect: (icon: string) => void;
  onRemove?: () => void;
  disabled?: boolean;
  icon?: string;
  size?: 'small' | 'medium' | 'large';
};

export const EmojiHandlerPopover = ({
  disabled,
  onSelect,
  onRemove,
  icon,
  size = 'medium',
}: EmojiHandlerPopoverProps) => {
  const { translate } = useLocales();
  return (
    <StyledContainer className={size}>
      {onRemove && icon && (
        <Tooltip
          title={translate('shared.buttons.remove_icon')}
          arrow
          placement="top"
        >
          <IconButton
            className={`removeIcon ${size}`}
            onClick={onRemove}
            disabled={disabled}
          >
            <StyledIconify icon="tabler:x" className={size} />
          </IconButton>
        </Tooltip>
      )}
      <PopoverTrigger
        popover={EmojiPickerPopover}
        context={{
          onSelectEmoji: (emoji: IEmoji) => onSelect(emoji.shortcodes),
          arrow: 'bottom-right',
        }}
        disabled={disabled}
      >
        <CustomIconButton
          size={size}
          className={cn({ hasIcon: !!icon })}
          disabled={disabled}
        >
          {!!icon ? (
            <em-emoji shortcodes={icon} />
          ) : (
            <StyledIconify icon="tabler:mood-plus" className={size} />
          )}
        </CustomIconButton>
      </PopoverTrigger>
    </StyledContainer>
  );
};

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'relative',

  '--icon-size': theme.spacing(5),
  '&.small': {
    '--icon-size': theme.spacing(3),
  },
  '&.large': {
    '--icon-size': theme.spacing(7.75),
  },
  width: 'var(--icon-size)',
  height: 'var(--icon-size)',
  '.hasIcon': {
    width: 'var(--icon-size)',
    height: 'var(--icon-size)',
  },

  '&:hover': {
    '.removeIcon': {
      opacity: 1,
    },
  },

  '.removeIcon': {
    '--remove-position': theme.spacing(-0.25),
    '--remove-size': theme.spacing(2.75),
    '&.small': {
      '--remove-position': theme.spacing(-0.75),
      '--remove-size': theme.spacing(2.5),
    },
    '&.large': {
      '--remove-position': theme.spacing(0.5),
      '--remove-size': theme.spacing(3),
    },
    position: 'absolute',
    padding: theme.spacing(0.5),
    opacity: 0,
    zIndex: 1,
    top: 'var(--remove-position)',
    right: 'var(--remove-position)',
    width: 'var(--remove-size)',
    height: 'var(--remove-size)',
  },
}));

const CustomIconButton = styled(IconButton)(() => ({
  flexShrink: 0,
  '&.MuiIconButton-sizeSmall': {
    padding: 0,
  },
  '&.hasIcon': {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

const StyledIconify = styled(Iconify)(({ theme }) => ({
  '--emot-size': theme.spacing(3),
  '&.small': {
    '--emot-size': theme.spacing(2.5),
  },
  '&.large': {
    '--emot-size': theme.spacing(4.75),
  },
  width: 'var(--emot-size)',
  height: 'var(--emot-size)',
}));
